import InfiniteScroll from "react-infinite-scroll-component";
import FilterComponent from "../components/dashboar-filter";
import TabComponent from "../components/tab-component";
import {
  BASE_URL,
  TAB_ROUTES,
  AUTO,
  FOOD,
  MEDICINE,
  CP,
  CHILD,
  PRESS,
  PET,
  CATEGORY_ID_MAP
} from "../assets/constant";
import { Loader } from "../components/loader";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { convertToSubcriberDate } from "../utils/helper";
import {
  AutoCard,
  ChildrenAndCPCard,
  PressCard,
  FoodAndMedicineCard,
  PetCard
} from "../components/dashboard-cards";
import { useSearchParams } from "react-router-dom";

const Dashboard = () => {
  const [data, setData] = useState({ data: [], total: null });
  const [dataLoadedCompletely, setDataLoadedCompletely] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useRef(1);
  const tabSelected = useRef(searchParams.get("tab") || AUTO);
  const isFavouriteActionInProgress = useRef(false);
  const user = useSelector((state) => state.auth);

  const fetchData = useCallback(
    async (resetPage = false) => {
      if (
        !tabSelected.current ||
        !isInitialFetchDone ||
        isFavouriteActionInProgress.current
      )
        return;
      try {
        setIsLoading(true);
        const params = Object.fromEntries(searchParams.entries());

        if (tabSelected.current === PET) {
          delete params.tab;

          const petParams = { ...params };
          if (petParams.from_date) {
            petParams.fromDate = petParams.from_date;
            delete petParams.from_date;
          }
          if (petParams.to_date) {
            petParams.toDate = petParams.to_date;
            delete petParams.to_date;
          }

          const queryParams = new URLSearchParams({
            page: resetPage ? 1 : page.current,
            limit: 20,
            ...petParams
          });

          const response = await axios.get(
            `${BASE_URL}/pet-recall/list?${queryParams.toString()}`,
            {
              headers: { Authorization: `Bearer ${user.token}` }
            }
          );

          if (response?.data?.success) {
            const petData = response.data.data.map(item => ({
              ...item,
              id: item.id,
              is_favourite: item.is_favourite || false
            }));

            setData((prev) => ({
              data: resetPage
                ? petData
                : [...prev.data, ...petData],
              total: response?.data?.pagination?.total || 0
            }));

            // Check if all data is loaded
            const totalPages = response?.data?.pagination?.totalPages || 0;
            const currentPage = response?.data?.pagination?.page || 1;
            if (currentPage >= totalPages || totalPages === 0) {
              setDataLoadedCompletely(true);
            } else {
              setDataLoadedCompletely(false);
            }

            page.current = resetPage ? 2 : page.current + 1;
          }
        } else {
          if (!params.from_date || !params.to_date) return;

          const response = await axios.post(
            `${BASE_URL}/${TAB_ROUTES[tabSelected.current]}`,
            {
              user_id: user.user.id,
              params: { page: resetPage ? 1 : page.current, ...params }
            },
            { headers: { Authorization: `Bearer ${user.token}` } }
          );

          const keyData = {
            [AUTO]: "autoData",
            [FOOD]: "foodData",
            [PRESS]: "pressData",
            [MEDICINE]: "drugData",
            [CHILD]: "childData",
            [CP]: "cpData"
          }[tabSelected.current];

          if (!response?.data?.[keyData]) setDataLoadedCompletely(true);
          setData((prev) => ({
            data: resetPage
              ? response?.data?.[keyData] ?? []
              : [...prev.data, ...(response?.data?.[keyData] || [])],
            total: response?.data?.total
          }));
          page.current = resetPage ? 2 : page.current + 1;
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [isInitialFetchDone, searchParams, user.user.id, user.token]
  );

  const updateTab = (value) => {
    const fDate = new Date();
    fDate.setFullYear(fDate.getFullYear() - 5);
    setSearchParams({
      tab: value,
      from_date: convertToSubcriberDate(fDate),
      to_date: convertToSubcriberDate(new Date())
    });
    setData({ data: [], total: null });
    tabSelected.current = value;
  };

  const handleFavourite = async (item) => {
    try {
      isFavouriteActionInProgress.current = true;
      await axios.post(
        `${BASE_URL}/v1.0/add-favourites`,
        {
          user_id: user.user.id,
          recall_id: item?.id || item?.RecallId,
          category_id: CATEGORY_ID_MAP[tabSelected.current]
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setData(function (prevData) {
        return {
          ...prevData,
          data: prevData.data.map(function (d) {
            const dataRecallId = d?.id || d?.RecallId;
            const itemRecallId = item?.id || item?.RecallId;
            const isIdMatch = String(dataRecallId) === String(itemRecallId);

            return isIdMatch
              ? { ...d, is_favourite: !Boolean(d.is_favourite) } // Toggle the favorite status
              : d;
          })
        };
      });
    } catch (error) {
      console.error(error);
    } finally {
      isFavouriteActionInProgress.current = false;
    }
  };

  useEffect(() => {
    const fDate = new Date();
    fDate.setFullYear(fDate.getFullYear() - 5);
    const queryParams = [
      "q",
      "manufacturer_or_make",
      "component",
      "model",
      "model_year",
      "classification",
      "from_date",
      "to_date",
      "source"
    ].reduce((acc, key) => {
      const value = searchParams.get(key);
      if (value) acc[key] = value;
      return acc;
    }, {});

    console.log('queryParams', queryParams);

    setSearchParams({
      tab: tabSelected.current,
      from_date: convertToSubcriberDate(fDate),
      to_date: convertToSubcriberDate(new Date()),
      ...queryParams
    });
    setIsInitialFetchDone(true);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (isInitialFetchDone) fetchData(true);
  }, [fetchData, isInitialFetchDone]);

  return (
    <div className="subscriber-container">
      <TabComponent updateTab={updateTab} isLoading={isLoading} />
      <FilterComponent
        tabSelected={tabSelected.current}
      />
      <InfiniteScroll
        dataLength={data.data.length}
        next={() => !isLoading && fetchData(false)}
        hasMore={!dataLoadedCompletely}
        loader={<Loader />}>
        <div className="myrecalllist">
          {data?.data?.map((item) => {
            const key = item.id || item.RecallId;
            const props = {
              key,
              item,
              handleFavourite,
              searchQuery: searchParams.get("q")
            };
            return (
              {
                [AUTO]: (
                  <AutoCard
                    {...props}
                    manufacturer_or_make={searchParams.get(
                      "manufacturer_or_make"
                    )}
                    component={searchParams.get("component")}
                    model={searchParams.get("model")}
                    model_year={searchParams.get("model_year")}
                  />
                ),
                [FOOD]: (
                  <FoodAndMedicineCard
                    {...props}
                    type={FOOD}
                    classification={searchParams.get("classification")}
                  />
                ),
                [MEDICINE]: (
                  <FoodAndMedicineCard
                    {...props}
                    type={MEDICINE}
                    classification={searchParams.get("classification")}
                  />
                ),
                [PRESS]: <PressCard {...props} />,
                [CHILD]: <ChildrenAndCPCard {...props} type={CHILD} />,
                [CP]: <ChildrenAndCPCard {...props} type={CP} />,
                [PET]: <PetCard {...props} />
              }[tabSelected.current] || null
            );
          })}
        </div>
        {data?.data?.length === 0 && !isLoading && (
          <div className="text-center mt-5">
            <h5>No Data Found</h5>
          </div>
        )}
        {isLoading && data?.data?.length === 0 && (
          <div className="text-center mt-5">
            <Loader />
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
};

export default memo(Dashboard);
